import React, { FC, useCallback, useEffect, useMemo } from "react";
import { Container, Grid, Theme, Typography } from "@material-ui/core";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { RouteComponentProps, useLocation } from "react-router";
import { useHistory } from "react-router-dom";
import { Helmet } from "react-helmet";
import * as queryString from "querystring";
import FooterWave from "../../../../assets/img/footer-wave.png";
import { endOfTitle } from "../../../../constants/constants";
import Loading from "../../../../components/Loading";
import Error from "../../../../components/Error";
import { mutateErrors } from "../../../../utils";
import { NotifyMode } from "../../../notify/NotifyModeEnum";
import { useNotify } from "../../../notify/useNotify";
import usePaymentQueries from "../../hooks/usePaymentQueries";
import { PackageCard } from "../../components/PackageCard";
import { usePayment } from "../../hooks/usePayment";
import Button from "../../../../components/Button";
import { useRecaptcha } from "../../../../hooks/useRecaptcha";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    minHeight: "70vh",
    backgroundImage: "linear-gradient(to bottom,#fff,#ece6d6)",
    position: "relative",
    paddingBottom: theme.spacing(4),

    "&:after": {
      content: "''",
      position: "absolute",
      top: "99.7%",
      height: 40,
      width: "100%",
      background: `url(${FooterWave}) top no-repeat`,
      backgroundSize: "100% 100%",
    },
  },
  productInfoContainer: {
    display: "flex",
    justifyContent: "center",
    columnGap: "50px",
  },
  submitButtonContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: 40,

    "& button": {
      width: "30%",
    },
  },
  noItemsContainer: {
    marginTop: 64,
  },
}));

const CheckoutPage: FC<RouteComponentProps> = ({ ...props }) => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const { executeRecaptcha } = useRecaptcha();

  const {
    loadProducts,
    loadingProducts,
    data: { cart, products } = {},
    actions: { addToCart, cleanCart, getRedirectUrl } = {},
  } = usePayment();

  const selectedPackageId = useMemo(() => {
    const selectedPackages =
      cart?.filter((product) => product.type === 1) || [];

    if (selectedPackages.length > 0) {
      return selectedPackages[0].id;
    }
  }, [cart]);

  const snackNotify = useNotify();

  const { createCheckoutSession } = usePaymentQueries();

  const selectPackage = useCallback(
    (id: number) => {
      if (selectedPackageId === id) return;

      cleanCart?.();
      addToCart?.(id);
    },
    [cleanCart, addToCart],
  );

  const handleSubmit = useCallback(
    async (data) => {
      try {
        const recaptchaToken = await executeRecaptcha(
          "create_product_checkout_session",
        );

        if (!cart || cart.length === 0) {
          snackNotify("Error", "Please select a package", NotifyMode.ERROR);
          return;
        }

        const {
          data: { redirectUrl },
        } = await createCheckoutSession(cart[0], recaptchaToken);

        // redirecting to payment checkout page
        window.location.replace(redirectUrl);
      } catch (e) {
        const { errors, message } = mutateErrors(e);
        snackNotify("Error", message, NotifyMode.ERROR);
      }
    },
    [createCheckoutSession, cart, executeRecaptcha],
  );

  const queryParams = queryString.parse(location.search.split("?").join(""));

  useEffect(() => {
    if (!queryParams.success) {
      loadProducts!();
    }
  }, [queryParams.success]); // eslint-disable-line react-hooks/exhaustive-deps

  // after redirecting back from payment page show success message and redirect to
  // previous page from where the student was redirected to our checkout page
  useEffect(() => {
    if (queryParams.success) {
      snackNotify("Success", "The payment was successful", NotifyMode.SUCCESS);

      cleanCart?.();

      if (getRedirectUrl!()) {
        history.push(getRedirectUrl!() || "");
      }
    }
  }, [queryParams.success]);

  // after pressing back button on payment page show warning message and keep previously selected package
  useEffect(() => {
    if (queryParams.canceled) {
      snackNotify("Canceled", "The payment was canceled", NotifyMode.WARNING);
    }
  }, [queryParams.canceled]);

  if (loadingProducts) {
    return <Loading />;
  }

  return (
    <div className={classes.root}>
      <Helmet>
        <title>Checkout{endOfTitle}</title>
      </Helmet>
      <Container maxWidth={"lg"}>
        <div className={classes.productInfoContainer}>
          {!!products?.length &&
            products
              .filter((product) => product.type === 1)
              .map((product) => (
                <PackageCard
                  key={product.id}
                  product={product}
                  selectedPackageId={selectedPackageId}
                  onClick={selectPackage}
                />
              ))}
        </div>
        {cart!.length === 0 ? (
          <Error
            fullHeight={false}
            text={"Please select a package"}
            className={classes.noItemsContainer}
          />
        ) : (
          <Grid container spacing={2} justify={"flex-start"}>
            <Grid item xs={12}>
              <div className={classes.submitButtonContainer}>
                <Button
                  onClick={handleSubmit}
                  type={"submit"}
                  color={"secondary"}
                  variant={"contained"}
                >
                  Proceed to Payment
                </Button>
              </div>
            </Grid>
          </Grid>
        )}
        <Typography component="p" align="center" style={{ marginTop: 16 }}>
          This site is protected by reCAPTCHA and the Google{" "}
          <a href="https://policies.google.com/privacy">Privacy Policy</a> and{" "}
          <a href="https://policies.google.com/terms">Terms of Service</a>{" "}
          apply.
        </Typography>
      </Container>
    </div>
  );
};

export default CheckoutPage;
